import { useStore } from '@/hooks/useStore';
import { LocalStorageActionTypes, LocalStorageGetterTypes } from '@/store/modules/localStorage';
import type {
  HokkaidoAirportReportLocalStorage,
  HokkaidoAirportState,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import {
  findOwnHokkaidoAirportStorage,
  isStaleDepartureStorage,
} from '@/components/Sp/ExternalReports/HokkaidoAirport/utils';

const REPORT_STORAGE_KEY = LocalStorageGetterTypes.HOKKAIDO_AIRPORT_REPORT_SP_STORAGE;

interface UseHokkaidoAirportReportStorageResult {
  saveLocalStorage: () => void;
  clearLocalStorage: () => void;
  // 報告書を復元できた場合は true
  restoreLocalStorage: () => boolean;
  stopLocalStorageSaving: () => void;
}

export const useHokkaidoAirportReportStorage = (
  state: HokkaidoAirportState,
): UseHokkaidoAirportReportStorageResult => {
  const store = useStore();
  const userState = store.state.user;

  // アンマウント後に返った通信の応答で、消した localStorage へ報告書を書き戻さないよう使う
  let isSavingStopped = false;
  const stopLocalStorageSaving = (): void => {
    isSavingStopped = true;
  };
  const saveLocalStorage = (): void => {
    if (isSavingStopped) return;

    const localStorage: HokkaidoAirportReportLocalStorage = {
      userId: userState.id,
      hokkaidoAirportCar: JSON.parse(JSON.stringify(state.hokkaidoAirportCar)),
      baseDepartureTs: state.baseDepartureTime,
      workStartTime: state.workStartTime,
    };
    store.dispatch(LocalStorageActionTypes.SET, { key: REPORT_STORAGE_KEY, val: localStorage });
  };
  const clearLocalStorage = (): void => {
    store.dispatch(LocalStorageActionTypes.SET, { key: REPORT_STORAGE_KEY, val: null });
  };
  const restoreLocalStorage = (): boolean => {
    const ownStorage = findOwnHokkaidoAirportStorage(store.getters[REPORT_STORAGE_KEY], userState.id);
    if (!ownStorage) {
      clearLocalStorage();
      return false;
    }

    // 業務終了の後、ログアウトの前にアプリを閉じた場合は終了済みの報告書が残るため、再開せずに破棄する
    if (isStaleDepartureStorage(ownStorage, new Date()) || ownStorage.hokkaidoAirportCar?.report?.baseArrivalTs) {
      clearLocalStorage();
      return false;
    }

    if (!ownStorage.hokkaidoAirportCar) return false;

    state.hokkaidoAirportCar = ownStorage.hokkaidoAirportCar;
    state.baseDepartureTime = ownStorage.baseDepartureTs ?? null;
    state.workStartTime = ownStorage.workStartTime ?? null;
    state.workEndTime = state.hokkaidoAirportCar.report.operationEndTs ?? null;
    return true;
  };

  return { saveLocalStorage, clearLocalStorage, restoreLocalStorage, stopLocalStorageSaving };
};
